import {
  AssetFamilySearchFilterEnum,
  EditorialProductEnum,
  IstockCollectionSearchFilterEnum,
  isCreativeAndEditorial,
  SearchParameterNameEnum,
  type SearchParameterName,
  type SearchParameters,
} from 'app/domain';

/**
 * Parameters the backend refills when the query omits them — search_params.rb,
 * param_filter.rb and search_overrides.rb each substitute a stored preference
 * or a profile default — so for these, silence is how the default gets back in.
 *
 * One hazard, two rules facing opposite ways: PRESERVES_EXPLICIT_FALSE keeps a
 * value alive reading in from Redux, URL_ONLY_SIGNALS states one writing out to
 * a url. Neither list implies the other.
 *
 * Not in the serializer: that also builds the filters wire request and the
 * asset-service query, which read an absent parameter as no filter.
 */

// The backend picks its own value when these are absent (search_params.rb
// #user_default_params, param_filter.rb#group_by_event), so an explicit false
// has to survive the read out of Redux.
export const PRESERVES_EXPLICIT_FALSE: ReadonlySet<string> = new Set([
  'excludenudity',
  'groupbyevent',
] satisfies SearchParameterName[]);

type UrlOnlySignal = {
  readonly name: SearchParameterName;
  readonly statedValue: string;
  readonly isStated: (
    searchParameters: SearchParameters,
    isOffered: (name: SearchParameterName) => boolean,
    previousSearchParameters: SearchParameters | undefined
  ) => boolean;
};

export const URL_ONLY_SIGNALS: readonly UrlOnlySignal[] = [
  {
    name: 'istockcollection',
    statedValue: IstockCollectionSearchFilterEnum.ALL,
    // All is the schema default, so the value alone says nothing; what makes it
    // worth stating is that the collection applies to this search. Two things
    // refill an absent one — the 30-day GlobalPreferences cookie and
    // apply_essentials_override — and both read silence as consent.
    isStated: (searchParameters, isOffered) =>
      searchParameters.istockcollection ===
        IstockCollectionSearchFilterEnum.ALL &&
      isOffered(SearchParameterNameEnum.ISTOCK_COLLECTION),
  },
  {
    name: 'editorialproducts',
    statedValue: EditorialProductEnum.ALL,
    // Only the change is stated: search_params.rb#build merges the cookied
    // category under the query, but once the previous search was All too,
    // silence says the same thing.
    isStated: (searchParameters, _isOffered, previousSearchParameters) =>
      !!previousSearchParameters &&
      searchParameters.editorialproducts === EditorialProductEnum.ALL &&
      previousSearchParameters.editorialproducts !== EditorialProductEnum.ALL,
  },
  {
    name: 'agreements',
    statedValue: '',
    // A diff, not a fact: "no agreements" is the resting state of most
    // searches, so only the change that cleared the last one can mean clear.
    isStated: (searchParameters, _isOffered, previousSearchParameters) =>
      !searchParameters.hasAgreements &&
      !!previousSearchParameters &&
      previousSearchParameters.hasAgreements,
  },
  {
    name: 'family',
    statedValue: AssetFamilySearchFilterEnum.CREATIVE_AND_EDITORIAL,
    // Both families is the schema default, but search_controller.rb#search_by_image
    // reads an absent family as creative, so a blended search by image has to
    // say so.
    isStated: (searchParameters) =>
      searchParameters.searchByImage &&
      isCreativeAndEditorial(searchParameters),
  },
];
